import images360Service from '@Dashboard/service/images360.service';
import { error as debugError } from '@/utils/debugLogger';
import { Image360 } from '@/scripts/js/forgeModels';
import { resolveTaskSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigation';

/**
 * Apply the same-scan rule + scan lookup for a given set of full tags. Returns
 * the data needed by share-intent players to decide between 3D and 360° flows.
 *
 * For 360° path: returns the scan and the first included tag so the canonical
 * executor can use the resolved scan provenance.
 *
 * Shares the routing decision used by Task/views/index.vue::showTasksOn3d and
 * retains its exact-scan lookup for the explicit 360 viewer action.
 *
 * @param {Array<object>} fullTags - Tags fetched via GET_BASE_TAGS_BY_IDS
 * @param {object} rootState - Root Vuex state (for projects)
 * @returns {{
 *   viewer: '3d' | '360',
 *   viewerTab: '3D View' | '360 View',
 *   scan: object | null,
 *   firstScanTag: object | null,
 *   plan: object | null
 * }}
 */
export async function deriveViewerState(fullTags, rootState) {
  const empty = {
    viewer: '3d',
    viewerTab: '3D View',
    scan: null,
    firstScanTag: null,
    plan: null,
  };

  if (!Array.isArray(fullTags) || fullTags.length === 0) return empty;

  const projects = rootState.projects || [];
  const plan = resolveTaskSpatialIntent(fullTags, {
    currentFacilityId: rootState.assetId,
    currentFacilityImageIds: projects.flatMap((project) => (
      project.images || []
    )).map((image) => image.id),
    intent: 'task-focus',
  });

  if (plan.kind !== 'viewer' || plan.viewer !== '360' || !plan.image360Id) {
    return { ...empty, plan };
  }

  let scan = projects
    .flatMap((p) => p.images || [])
    .find((img) => img.id === plan.image360Id);

  if (!scan) {
    try {
      const { data } = await images360Service.getById(plan.image360Id, plan.facilityId);
      if (data) {
        scan = Image360.fromDTO(data);
      }
    } catch (err) {
      debugError(`deriveViewerState: 360 API lookup failed for image360Id=${plan.image360Id}`, err);
    }
  }

  if (!scan) {
    // A stale resolved scan (deleted post-tagging) falls back to 3D
    // rather than guessing — preserves the "no surprises" contract from the
    // coherent same-scan rule.
    return { ...empty, plan };
  }

  return {
    viewer: '360',
    viewerTab: '360 View',
    scan,
    firstScanTag: fullTags.find((tag) => (
      !Array.isArray(plan.includedTagIds) || plan.includedTagIds.includes(tag.id)
    )) || fullTags[0],
    plan,
  };
}
